import { useState, useEffect, useCallback, useRef } from 'react'
import { useSearchParams, useParams, useNavigate } from 'react-router-dom'
import { authFetch } from '../lib/authFetch'
import { FilterSidebar } from '../components/FilterSidebar'
import { ProjectsTable } from '../components/ProjectsTable'
import { ProjectDetail } from '../components/ProjectDetail'

import { useDebounce } from '../hooks/useDebounce'
import type { ProjectListItemV2, ProjectFiltersV2, ProjectListResponseV2, Pagination } from '../types'

const DEFAULT_FILTERS: ProjectFiltersV2 = {
  domain: [],
  pipeline_status: [],
  score_min: null,
  score_max: null,
  scored: null,
  owner: [],
  institution: [],
  industry: [],
  q: '',
  dev_phase: [],
  therapeutic_area: [],
  modality: [],
  source_class: [],
  city: [],
  competitive_category: [],
}

const DEFAULT_PAGINATION: Pagination = { page: 1, limit: 25, total: 0, pages: 0 }

function buildQuery(
  filters: ProjectFiltersV2,
  sort: string,
  order: 'asc' | 'desc',
  page: number,
): string {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('limit', '25')
  if (sort) {
    params.set('sort', sort)
    params.set('order', order)
  }

  if (filters.domain.length > 0) {
    params.set('domain', filters.domain.join(','))
  }
  if (filters.pipeline_status.length > 0) {
    params.set('pipeline_status', filters.pipeline_status.join(','))
  }
  if (filters.score_min !== null) {
    params.set('score_min', String(filters.score_min))
  }
  if (filters.score_max !== null) {
    params.set('score_max', String(filters.score_max))
  }
  if (filters.scored !== null) {
    params.set('scored', String(filters.scored))
  }
  if (filters.owner.length > 0) {
    params.set('owner', filters.owner.join(','))
  }
  if (filters.institution.length > 0) {
    params.set('institution', filters.institution.join(','))
  }
  if (filters.industry.length > 0) {
    params.set('industry', filters.industry.join(','))
  }
  if (filters.q) {
    params.set('q', filters.q)
  }
  if (filters.dev_phase.length > 0) {
    params.set('dev_phase', filters.dev_phase.join(','))
  }
  if (filters.therapeutic_area.length > 0) {
    params.set('therapeutic_area', filters.therapeutic_area.join(','))
  }
  if (filters.modality.length > 0) {
    params.set('modality', filters.modality.join(','))
  }
  if (filters.source_class.length > 0) {
    params.set('source_class', filters.source_class.join(','))
  }
  if (filters.city.length > 0) {
    params.set('city', filters.city.join(','))
  }
  if (filters.competitive_category.length > 0) {
    params.set('competitive_category', filters.competitive_category.join(','))
  }

  return params.toString()
}

export function ProjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { id: routeProjectId } = useParams<{ id?: string }>()
  const navigate = useNavigate()

  // Support both /projects/:id and /projects?project=<id>
  const selectedProjectId = routeProjectId || searchParams.get('project')

  const [filters, setFilters] = useState<ProjectFiltersV2>(DEFAULT_FILTERS)
  const [sort, setSort] = useState('updated_at')
  const [order, setOrder] = useState<'asc' | 'desc'>('desc')
  const [page, setPage] = useState(1)

  const [projects, setProjects] = useState<ProjectListItemV2[]>([])
  const [pagination, setPagination] = useState<Pagination>(DEFAULT_PAGINATION)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearch = useDebounce(searchQuery, 300)

  const abortRef = useRef<AbortController | null>(null)

  const fetchProjects = useCallback(
    async (
      currentFilters: ProjectFiltersV2,
      currentSort: string,
      currentOrder: 'asc' | 'desc',
      currentPage: number,
    ) => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl

      setLoading(true)
      try {
        const qs = buildQuery(currentFilters, currentSort, currentOrder, currentPage)
        const res = await authFetch(`/api/v2/projects?${qs}`)
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = (await res.json()) as ProjectListResponseV2
        if (!ctrl.signal.aborted) {
          setProjects(data.data)
          setPagination(data.pagination)
        }
      } catch (e) {
        if (!ctrl.signal.aborted) console.error('Failed to fetch projects:', e)
      } finally {
        if (!ctrl.signal.aborted) setLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    void fetchProjects(filters, sort, order, page)
  }, [filters, sort, order, page, fetchProjects])

  // Sync debounced search → filters.q
  useEffect(() => {
    setFilters(prev => {
      if (prev.q === debouncedSearch) return prev
      setPage(1)
      return { ...prev, q: debouncedSearch }
    })
  }, [debouncedSearch])

  const handleSort = (column: string) => {
    if (column === sort) {
      setOrder(o => (o === 'asc' ? 'desc' : 'asc'))
    } else {
      setSort(column)
      setOrder('asc')
    }
    setPage(1)
  }

  const handleFiltersChange = (newFilters: ProjectFiltersV2) => {
    // Sync search input if sidebar clears q
    if (newFilters.q !== filters.q) {
      setSearchQuery(newFilters.q)
    }
    setFilters(newFilters)
    setPage(1)
  }

  const handleRowClick = (project: ProjectListItemV2) => {
    navigate(`/projects/${project.id}`)
  }

  const handleModalClose = () => {
    navigate('/projects')
  }

  const handleProjectUpdate = (_updated: any) => {
    // ProjectDetail returns Project type, we need to refetch or match IDs
    // For now, we'll just let the local state stay as-is since detail updates
    // typically happen in the modal only
  }

  // Full content area: show detail OR list, not both
  if (selectedProjectId) {
    return (
      <div className="h-full overflow-hidden">
        <ProjectDetail
          projectId={selectedProjectId}
          onClose={handleModalClose}
          onUpdate={handleProjectUpdate}
        />
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden p-2 gap-2">
      <FilterSidebar filters={filters} onChange={handleFiltersChange} />

      <div className="flex-1 min-w-0 overflow-y-auto bg-bg-surface border border-border-subtle rounded-lg">
        <div className="px-4 py-3">
          <ProjectsTable
            projects={projects}
            pagination={pagination}
            loading={loading}
            sort={sort}
            order={order}
            onSort={handleSort}
            onPageChange={setPage}
            onRowClick={handleRowClick}
            searchValue={searchQuery}
            onSearchChange={setSearchQuery}
          />
        </div>
      </div>
    </div>
  )
}
